
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { TrainingFormValues } from '../../types';
import { trainingFormSchema, getDefaultValues } from './schema';
import { useTrainingForm } from './hooks/useTrainingForm';
import { useUnsavedChanges } from './hooks/useUnsavedChanges';

import { SessionTypeSection } from './sections/SessionTypeSection';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { TrainingDetailsSection } from './sections/TrainingDetailsSection';
import { TechniquesSection } from './sections/TechniquesSection';
import { YouTubeUrlSection } from './sections/YouTubeUrlSection';
import { CompetitionSection } from './sections/CompetitionSection';
import { NotesSection } from './sections/NotesSection';
import { usePermissions } from '@/contexts/unified-permissions';
import { DraftConfirmationDialog } from './components/DraftConfirmationDialog';
import { loadFormDraft, hasMeaningfulDraft } from '../../utils/storageUtils';
import { toast } from 'sonner';

interface TrainingFormProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialValues?: Partial<TrainingFormValues>;
  onSubmit?: (data: TrainingFormValues) => void;
  onCancel?: () => void;
  isEditing?: boolean;
  sessionId?: string;
}

export function TrainingForm({
  initialValues,
  onSubmit,
  onCancel,
  isEditing = false,
  sessionId,
}: TrainingFormProps) {
  const { user } = usePermissions();
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  
  // Extract only needed user metadata to prevent unnecessary re-renders
  const userBeltRank = user?.user_metadata?.belt_rank || 'white';
  const userStripes = user?.user_metadata?.stripes || user?.user_metadata?.stripe_count;
  
  const defaultValues = useMemo(() => {
    // Convert stripe count to proper format
    let stripeCount = 'no stripes';
    if (userStripes !== undefined && userStripes !== null) {
      const stripeNum = typeof userStripes === 'string' ? parseInt(userStripes, 10) : userStripes;
      if (!isNaN(stripeNum) && stripeNum > 0) {
        stripeCount = stripeNum.toString();
      }
    }

    return {
      ...getDefaultValues(isEditing),
      belt_rank: userBeltRank,
      stripe_count: stripeCount,
      ...initialValues,
    };
  }, [initialValues, userBeltRank, userStripes, isEditing]);

  const form = useForm<TrainingFormValues>({
    resolver: zodResolver(trainingFormSchema),
    defaultValues,
    mode: 'onBlur', // Changed from 'onChange' to reduce re-renders
  });
  
  const { isSubmittingForm, handleSubmit } = useTrainingForm({
    initialValues,
    onSubmit,
    isEditing,
    sessionId,
  });
  
  const { hasUnsavedChanges, resetUnsavedChanges } = useUnsavedChanges({
    form,
    isSubmitting: isSubmittingForm
  });

  // Use useWatch for specific field instead of form.watch()
  const sessionType = useWatch({
    control: form.control,
    name: 'sessionType'
  });

  // Check for meaningful draft on component mount (only for new sessions)
  useEffect(() => {
    if (!isEditing && hasMeaningfulDraft()) {
      setShowDraftDialog(true);
    }
  }, [isEditing]);

  const handleLoadDraft = useCallback(() => {
    const draft = loadFormDraft();
    if (draft) {
      const mergedValues = {
        ...defaultValues,
        ...draft,
        date: draft.date || defaultValues.date
      };
      form.reset(mergedValues);
      toast.success('Draft loaded successfully');
    }
    setShowDraftDialog(false);
  }, [defaultValues, form]);



  const handleCancelClick = useCallback(() => {
    if (hasUnsavedChanges && !isEditing) {
      setShowUnsavedDialog(true);
    } else {
      onCancel?.();
    }
  }, [hasUnsavedChanges, isEditing, onCancel]);

  const handleConfirmClose = useCallback(() => {
    setShowUnsavedDialog(false);
    resetUnsavedChanges();
    onCancel?.();
  }, [resetUnsavedChanges, onCancel]);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    form.handleSubmit(handleSubmit)(e);
  }, [form, handleSubmit]);

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={handleFormSubmit} className="space-y-6">
          <SessionTypeSection form={form} />
          <BasicInfoSection form={form} />
          <TrainingDetailsSection form={form} />
          <YouTubeUrlSection form={form} />

          {sessionType === 'Competition' && (
            <CompetitionSection form={form} />
          )}

          <TechniquesSection form={form} />
          <NotesSection form={form} />

          <div className="flex justify-end gap-4 pt-4">
            {onCancel && (
              <Button type="button" variant="outline" onClick={handleCancelClick}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmittingForm}
              className="relative"
            >
              {isSubmittingForm && (
                <Loader2 className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
              )}
              {isEditing ? 'Update Session' : 'Add Session'}
            </Button>
          </div>
        </form>
      </Form>

      <DraftConfirmationDialog
        open={showDraftDialog}
        onOpenChange={setShowDraftDialog}
        onConfirm={handleLoadDraft}
        title="Resume Previous Session?"
        description="You have unsaved training session data. Would you like to continue where you left off?"
        confirmLabel="Load Draft"
        cancelLabel="Start Fresh"
      />

      <DraftConfirmationDialog
        open={showUnsavedDialog}
        onOpenChange={setShowUnsavedDialog}
        onConfirm={handleConfirmClose}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you close this form. Are you sure you want to continue?"
        confirmLabel="Discard Changes"
        cancelLabel="Keep Editing"
      />
    </div>
  );
}
